/**
 * Personalize a template — pick a project, auto-fill merge fields, copy.
 *
 * Fetches the installer's own projects lazily on open and pre-fills merge
 * tokens like {{customer_name}}, {{project_ref}}, {{install_date}} etc.
 * The rendered body remains editable so installers can tweak wording
 * before copying — the Copy button always copies the current text.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Sparkles, X } from 'lucide-react';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MERGE_FIELDS, buildMergeContext, renderMergeFields } from '@/utils/mergeFields';

export default function PersonalizeTemplateModal({ template, onClose }) {
  const [projects, setProjects] = useState([]);
  const [user, setUser] = useState(null);
  const [projectId, setProjectId] = useState('');
  const [loading, setLoading] = useState(true);
  const [rendered, setRendered] = useState(template?.body_text || '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [projRes, meRes] = await Promise.all([
          fetch(`${API_URL}/api/projects`, { credentials: 'include' }),
          fetch(`${API_URL}/api/auth/me`, { credentials: 'include' }),
        ]);
        if (cancelled) return;
        const projList = projRes.ok ? await projRes.json() : [];
        setProjects(Array.isArray(projList) ? projList : []);
        setUser(meRes.ok ? await meRes.json() : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === projectId || p.project_id === projectId) || null,
    [projects, projectId],
  );

  useEffect(() => {
    const ctx = buildMergeContext({ project: selectedProject, user });
    setRendered(renderMergeFields(template?.body_text || '', ctx));
  }, [selectedProject, user, template]);

  const copyRendered = async () => {
    try {
      await navigator.clipboard.writeText(rendered);
      toast.success('Copied — paste into your email');
    } catch {
      toast.error('Copy failed');
    }
  };

  const insertToken = (token) => {
    setRendered((cur) => `${cur}${cur.endsWith(' ') || cur.length === 0 ? '' : ' '}{{${token}}}`);
  };

  if (!template) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" /> Personalize: {template.title}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="personalize-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold" htmlFor="personalize-project">Pick a project to auto-fill</Label>
            <select
              id="personalize-project"
              data-testid="personalize-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm mt-1"
              disabled={loading}
            >
              <option value="">{loading ? 'Loading projects…' : projects.length ? '— select a project —' : 'No projects available'}</option>
              {projects.map((p) => {
                const id = p.id || p.project_id;
                const label = [p.reference, p.customer_name].filter(Boolean).join(' · ') || id;
                return <option key={id} value={id}>{label}</option>;
              })}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">Or edit the text below and copy — merge fields still work if you type <code className="bg-gray-100 px-1 rounded">{'{{customer_name}}'}</code>.</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold">Rendered body</Label>
              <div className="flex flex-wrap gap-1">
                {MERGE_FIELDS.map((f) => (
                  <button
                    key={f.token}
                    type="button"
                    onClick={() => insertToken(f.token)}
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                    title={f.label}
                    data-testid={`personalize-insert-${f.token}`}
                  >
                    {`{{${f.token}}}`}
                  </button>
                ))}
              </div>
            </div>
            <Textarea
              rows={12}
              value={rendered}
              onChange={(e) => setRendered(e.target.value)}
              className="font-mono text-sm"
              data-testid="personalize-body"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={copyRendered} className="bg-gray-900 hover:bg-black text-white" data-testid="personalize-copy">
            <Copy className="w-4 h-4 mr-1.5" /> Copy to clipboard
          </Button>
        </div>
      </div>
    </div>
  );
}
